/**
 * Product search + variant picker for the redesign quote form (1-quote-form.liquid).
 * Works with a native Shopify {% form 'contact' %} — no shadow DOM.
 *
 * Flow:
 *  1. User types in #psw-quote-search → autocomplete via /search/suggest.json
 *  2. Selecting a result fetches /products/{handle}.json for variants
 *  3. If 1 variant (Default Title) → add chip immediately
 *  4. If multiple variants → fetch metafield (?view=1-quote-meta)
 *     - variant_required=true  → must choose a variant before adding
 *     - variant_required=false → can add without variant, or pick one
 *  5. Chips rendered in #psw-quote-chips; hidden inputs in #psw-quote-hidden-inputs
 */
(function () {
  'use strict';

  var SEARCH_LIMIT = 6;
  var DEBOUNCE_MS  = 280;
  var ORIGIN       = window.location.origin;

  // State
  var products     = []; // [{ productId, productTitle, variantId, variantTitle, imageUrl }]
  var debounceTimer = null;
  var variantRequired = false;

  // DOM refs (set in init)
  var searchInput, resultsEl, chipsEl, variantWrapEl, hiddenInputsEl, addMoreBtn, searchWrapEl;

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  function init() {
    searchInput    = document.getElementById('psw-quote-search');
    resultsEl      = document.getElementById('psw-quote-results');
    chipsEl        = document.getElementById('psw-quote-chips');
    variantWrapEl  = document.getElementById('psw-quote-variant-wrap');
    hiddenInputsEl = document.getElementById('psw-quote-hidden-inputs');
    addMoreBtn     = document.getElementById('psw-quote-add-more');
    searchWrapEl   = document.getElementById('psw-quote-search-wrap');

    if (!searchInput || !resultsEl) return;

    // Search input events
    searchInput.addEventListener('input', function () {
      var q = searchInput.value.trim();
      if (q.length < 2) { hideResults(); return; }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { performSearch(q); }, DEBOUNCE_MS);
    });

    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { hideResults(); searchInput.blur(); }
    });

    // Close dropdown on outside click
    document.addEventListener('click', function (e) {
      if (!searchWrapEl.contains(e.target)) hideResults();
    });

    // "Add another" button
    if (addMoreBtn) {
      addMoreBtn.addEventListener('click', function () {
        addMoreBtn.hidden = true;
        searchWrapEl.hidden = false;
        searchInput.focus();
      });
    }

    // URL param prefill
    var params = new URLSearchParams(window.location.search);
    var preTitle = params.get('product_title');
    var preHandle = params.get('product_handle');
    var preVariantId = params.get('variant_id');
    var bundleLabel = params.get('bundle_label');
    if (preHandle) {
      prefillFromHandle(preHandle, preTitle, preVariantId, bundleLabel || null);
    } else if (preTitle) {
      prefillFromParams(decodeURIComponent(preTitle), preVariantId);
    }
    var preTitle2 = params.get('product_title_2');
    var preHandle2 = params.get('product_handle_2');
    var preVariantId2 = params.get('variant_id_2');
    if (preHandle2) {
      prefillFromHandle(preHandle2, preTitle2, preVariantId2);
    } else if (preTitle2) {
      prefillFromParams(decodeURIComponent(preTitle2), preVariantId2);
    }
    var preTitle3 = params.get('product_title_3');
    var preHandle3 = params.get('product_handle_3');
    var preVariantId3 = params.get('variant_id_3');
    if (preHandle3) {
      prefillFromHandle(preHandle3, preTitle3, preVariantId3);
    } else if (preTitle3) {
      prefillFromParams(decodeURIComponent(preTitle3), preVariantId3);
    }

    // Prefill bundle + addon selections from URL params (set by TT buy box)
    var bundleParam = params.get('bundle');
    var addonParams = params.getAll('addon');
    if (bundleParam || addonParams.length) {
      var extraInputsEl = document.getElementById('psw-quote-hidden-inputs');
      if (extraInputsEl) {
        if (bundleParam) {
          var bInp = document.createElement('input');
          bInp.type = 'hidden';
          bInp.name = 'contact[bundle]';
          bInp.value = decodeURIComponent(bundleParam);
          extraInputsEl.appendChild(bInp);
        }
        if (addonParams.length) {
          var aInp = document.createElement('input');
          aInp.type = 'hidden';
          aInp.name = 'contact[addons]';
          aInp.value = addonParams.map(decodeURIComponent).join(', ');
          extraInputsEl.appendChild(aInp);
        }
      }
    }

    renderUI();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  function performSearch(q) {
    var url = ORIGIN + '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product&resources%5Blimit%5D=' + SEARCH_LIMIT;
    xhr(url, function (data) {
      var items = (data.resources && data.resources.results && data.resources.results.products) || [];
      renderResults(items);
    }, function () { /* silent on error */ });
  }

  function renderResults(items) {
    if (!items.length) {
      resultsEl.innerHTML = '<div class="psw-qfs-no-results">No products found</div>';
      resultsEl.hidden = false;
      return;
    }
    var html = '';
    items.forEach(function (p) {
      var imgUrl = (p.featured_image && p.featured_image.url) ? p.featured_image.url + '&width=80' : '';
      html += '<button type="button" class="psw-qfs-result"' +
        ' data-handle="' + esc(p.handle) + '"' +
        ' data-title="'  + esc(p.title)  + '"' +
        ' data-image="'  + esc(imgUrl)   + '">' +
        (imgUrl
          ? '<img class="psw-qfs-result-img" src="' + esc(imgUrl) + '" alt="" width="36" height="36">'
          : '<span class="psw-qfs-result-img-ph"></span>') +
        '<span class="psw-qfs-result-title">' + escHTML(p.title) + '</span>' +
        '</button>';
    });
    resultsEl.innerHTML = html;
    resultsEl.hidden = false;
    resultsEl.querySelectorAll('.psw-qfs-result').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        onProductSelected(btn.dataset.handle, btn.dataset.title, btn.dataset.image);
      });
    });
  }

  function hideResults() {
    resultsEl.hidden = true;
    resultsEl.innerHTML = '';
  }

  // ── Product selection + variant flow ──────────────────────────────────────

  function onProductSelected(handle, title, imageUrl) {
    hideResults();
    searchInput.value = '';
    searchWrapEl.hidden = true;

    // Fetch full product data (variants)
    xhr(ORIGIN + '/products/' + handle + '.json', function (data) {
      var p = data.product;
      var variants = p.variants || [];
      var productId = String(p.id);
      if (!imageUrl && p.images && p.images.length) {
        imageUrl = p.images[0].src + '&width=80';
      }

      if (variants.length === 1) {
        // Single variant — add directly
        addProduct({
          productId: productId,
          productTitle: title,
          variantId: String(variants[0].id),
          variantTitle: variants[0].title === 'Default Title' ? '' : variants[0].title,
          imageUrl: imageUrl
        });
      } else {
        // Multiple variants — check metafield then show picker
        fetchVariantRequired(handle, function (required) {
          variantRequired = required;
          showVariantPicker(variants, productId, title, imageUrl, required);
        });
      }
    }, function () {
      // On error just restore search
      searchWrapEl.hidden = false;
    });
  }

  function fetchVariantRequired(handle, callback) {
    xhr(ORIGIN + '/products/' + handle + '?view=1-quote-meta', function (data) {
      callback(!!data.variant_required);
    }, function () { callback(false); });
  }

  function showVariantPicker(variants, productId, productTitle, imageUrl, required) {
    var options = variants.map(function (v) {
      return '<option value="' + v.id + '" data-title="' + esc(v.title) + '">' + escHTML(v.title) + '</option>';
    }).join('');

    variantWrapEl.innerHTML =
      '<div class="psw-qfs-variant-picker">' +
        '<label class="psw-qfs-variant-label">Select an option for <strong>' + escHTML(productTitle) + '</strong>' +
          (required ? '' : ' <span class="psw-qfs-variant-optional">(optional)</span>') +
        '</label>' +
        '<p class="psw-qfs-variant-required-msg" hidden>Please select an option to continue.</p>' +
        '<select class="psw-qfs-variant-select">' +
          '<option value="">Choose an option…</option>' + options +
        '</select>' +
        '<div class="psw-qfs-variant-actions">' +
          '<button type="button" class="psw-qfs-variant-confirm">Add to quote</button>' +
          (!required ? '<button type="button" class="psw-qfs-variant-skip">Add without option</button>' : '') +
          '<button type="button" class="psw-qfs-variant-cancel">Cancel</button>' +
        '</div>' +
      '</div>';
    variantWrapEl.hidden = false;

    var select   = variantWrapEl.querySelector('.psw-qfs-variant-select');
    var reqMsg   = variantWrapEl.querySelector('.psw-qfs-variant-required-msg');
    var confirmBtn = variantWrapEl.querySelector('.psw-qfs-variant-confirm');
    var skipBtn  = variantWrapEl.querySelector('.psw-qfs-variant-skip');
    var cancelBtn  = variantWrapEl.querySelector('.psw-qfs-variant-cancel');

    select.addEventListener('change', function () {
      select.classList.remove('psw-qfs-variant-select--invalid');
      reqMsg.hidden = true;
    });

    confirmBtn.addEventListener('click', function () {
      var opt = select.options[select.selectedIndex];
      if (!opt || !opt.value) {
        if (required) {
          select.classList.add('psw-qfs-variant-select--invalid');
          reqMsg.hidden = false;
          return;
        }
        // Not required — add without variant
        addProductAndCloseVariantPicker(productId, productTitle, '', '', imageUrl);
        return;
      }
      addProductAndCloseVariantPicker(productId, productTitle, opt.value, opt.dataset.title || opt.textContent, imageUrl);
    });

    if (skipBtn) {
      skipBtn.addEventListener('click', function () {
        addProductAndCloseVariantPicker(productId, productTitle, '', '', imageUrl);
      });
    }

    cancelBtn.addEventListener('click', function () {
      hideVariantPicker();
      searchWrapEl.hidden = false;
    });
  }

  function addProductAndCloseVariantPicker(productId, productTitle, variantId, variantTitle, imageUrl) {
    hideVariantPicker();
    addProduct({ productId: productId, productTitle: productTitle, variantId: variantId, variantTitle: variantTitle, imageUrl: imageUrl });
  }

  function hideVariantPicker() {
    variantWrapEl.hidden = true;
    variantWrapEl.innerHTML = '';
    variantRequired = false;
  }

  // ── Product list ──────────────────────────────────────────────────────────

  function addProduct(item) {
    // Deduplicate
    for (var i = 0; i < products.length; i++) {
      var ex = products[i];
      if (item.variantId && ex.variantId === item.variantId) return;
      if (!item.variantId && !ex.variantId && ex.productId === item.productId) return;
    }
    products.push(item);
    syncHiddenInputs();
    renderUI();
  }

  function removeProduct(index) {
    products.splice(index, 1);
    syncHiddenInputs();
    renderUI();
  }

  function syncHiddenInputs() {
    if (!hiddenInputsEl) return;
    hiddenInputsEl.innerHTML = '';
    if (!products.length) return;
    var lines = products.map(function (p, i) {
      var label = (i + 1) + '. ' + p.productTitle + (p.variantTitle ? ' \u2014 ' + p.variantTitle : '');
      if (p.variantId) label += ' (ID: ' + p.variantId + ')';
      return label;
    });
    var inp = document.createElement('input');
    inp.type = 'hidden';
    inp.name = 'contact[products]';
    inp.value = lines.join('\n');
    hiddenInputsEl.appendChild(inp);
  }

  function renderUI() {
    renderChips();
    var hasProducts = products.length > 0;
    if (hasProducts) {
      // Show "add more" button, hide search (unless variant picker is open)
      if (variantWrapEl.hidden) {
        addMoreBtn.hidden = false;
        searchWrapEl.hidden = true;
      }
    } else {
      // No products — show search, hide "add more"
      addMoreBtn.hidden = true;
      searchWrapEl.hidden = false;
      searchInput.focus();
    }
  }

  function renderChips() {
    if (!chipsEl) return;
    if (!products.length) { chipsEl.innerHTML = ''; return; }
    var html = '';
    products.forEach(function (p, i) {
      html += '<div class="psw-qfs-chip">' +
        (p.imageUrl
          ? '<img class="psw-qfs-chip-img" src="' + esc(p.imageUrl) + '" alt="" width="36" height="36">'
          : '<span class="psw-qfs-chip-img-ph"></span>') +
        '<span class="psw-qfs-chip-title">' + escHTML(p.productTitle) +
          (p.variantTitle ? ' <span class="psw-qfs-chip-variant">\u2014 ' + escHTML(p.variantTitle) + '</span>' : '') +
        '</span>' +
        '<button type="button" class="psw-qfs-chip-remove" data-index="' + i + '" aria-label="Remove ' + esc(p.productTitle) + '">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
      '</div>';
    });
    chipsEl.innerHTML = html;
    chipsEl.querySelectorAll('.psw-qfs-chip-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeProduct(parseInt(btn.dataset.index, 10));
      });
    });
  }

  // ── URL param prefill ─────────────────────────────────────────────────────

  // Prefill using a known product handle — fetches directly, no fuzzy search.
  // Works for unlisted/draft products that don't appear in search suggestions.
  // variantLabel: optional string shown as chip subtitle (e.g. bundle name from TT buy box)
  function prefillFromHandle(handle, fallbackTitle, variantId, variantLabel) {
    xhr(ORIGIN + '/products/' + handle + '.json', function (data) {
      var p = data.product;
      var imgUrl = (p.images && p.images.length) ? p.images[0].src + '&width=80' : '';
      var title = p.title || fallbackTitle || handle;
      var productId = String(p.id);
      var chipLabel = variantLabel || '';
      if (!variantId) {
        addProduct({ productId: productId, productTitle: title, variantId: '', variantTitle: chipLabel, imageUrl: imgUrl });
        return;
      }
      var variants = p.variants || [];
      var vTitle = '';
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) {
          vTitle = variants[i].title === 'Default Title' ? '' : variants[i].title;
          break;
        }
      }
      addProduct({ productId: productId, productTitle: title, variantId: variantId, variantTitle: chipLabel || vTitle, imageUrl: imgUrl });
    }, function () {
      // Handle fetch failed — fall back to title search
      if (fallbackTitle) prefillFromParams(fallbackTitle, variantId);
    });
  }

  function prefillFromParams(productTitle, variantId) {
    if (!productTitle) return;
    // Search for the product by title to get handle/image
    var url = ORIGIN + '/search/suggest.json?q=' + encodeURIComponent(productTitle) +
      '&resources%5Btype%5D=product&resources%5Blimit%5D=1';
    xhr(url, function (data) {
      var items = (data.resources && data.resources.results && data.resources.results.products) || [];
      if (!items.length) {
        // Fallback: add just by title
        addProduct({ productId: '', productTitle: productTitle, variantId: variantId || '', variantTitle: '', imageUrl: '' });
        return;
      }
      var p = items[0];
      var imgUrl = (p.featured_image && p.featured_image.url) ? p.featured_image.url + '&width=80' : '';
      if (!variantId) {
        addProduct({ productId: String(p.id || ''), productTitle: p.title, variantId: '', variantTitle: '', imageUrl: imgUrl });
        return;
      }
      // Resolve variant title
      xhr(ORIGIN + '/products/' + p.handle + '.json', function (pd) {
        var variants = pd.product.variants || [];
        var vTitle = '';
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(variantId)) {
            vTitle = variants[i].title === 'Default Title' ? '' : variants[i].title;
            break;
          }
        }
        addProduct({ productId: String(pd.product.id), productTitle: p.title, variantId: variantId, variantTitle: vTitle, imageUrl: imgUrl });
      }, function () {
        addProduct({ productId: '', productTitle: productTitle, variantId: variantId, variantTitle: '', imageUrl: imgUrl });
      });
    }, function () {
      addProduct({ productId: '', productTitle: productTitle, variantId: variantId || '', variantTitle: '', imageUrl: '' });
    });
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  function xhr(url, onSuccess, onError) {
    var req = new XMLHttpRequest();
    req.open('GET', url, true);
    req.onload = function () {
      if (req.status >= 200 && req.status < 300) {
        try { onSuccess(JSON.parse(req.responseText)); } catch (e) { if (onError) onError(e); }
      } else {
        if (onError) onError(new Error('HTTP ' + req.status));
      }
    };
    req.onerror = function () { if (onError) onError(new Error('Network error')); };
    req.send();
  }

  function escHTML(s) {
    var d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  function esc(s) {
    return (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
